import React, { useEffect, useRef, useState } from 'react';
import {
    SUPPORTED_LOCALES,
    SupportedLocale,
    LOCALE_META,
} from '../../i18n/config';
import { useLocale } from '../../hooks/useLocale';
import './LocaleSwitcher.scss';

interface Props {
    /**
     * Constrain the options to this subset. When omitted, show all supported locales.
     * Use the constrained form for logged-in users (company.active_locales); omit on
     * the login page where no company context exists yet.
     */
    availableLocales?: SupportedLocale[];
    /** Hide when only one locale is selectable — nothing to switch between. */
    hideWhenSingle?: boolean;
    className?: string;
}

const LocaleSwitcher: React.FC<Props> = ({ availableLocales, hideWhenSingle = true, className }) => {
    const { locale, setLocale } = useLocale();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);

    const options: SupportedLocale[] = availableLocales && availableLocales.length
        ? availableLocales
        : (SUPPORTED_LOCALES as readonly SupportedLocale[]).slice();

    useEffect(() => {
        function onClick(e: MouseEvent) {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target as Node)) setOpen(false);
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false);
        }
        if (open) {
            document.addEventListener('mousedown', onClick);
            document.addEventListener('keydown', onKey);
        }
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    if (hideWhenSingle && options.length <= 1) return null;

    const current = LOCALE_META[locale];

    return (
        <div ref={rootRef} className={`locale-switcher ${className ?? ''}`}>
            <button
                type="button"
                className="locale-switcher__trigger"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className="locale-switcher__globe" aria-hidden>🌐</span>
                <span className="locale-switcher__label">{current?.native ?? locale}</span>
                <span className="locale-switcher__caret" aria-hidden>▾</span>
            </button>
            {open && (
                <ul className="locale-switcher__menu" role="listbox">
                    {options.map((code) => {
                        const meta = LOCALE_META[code];
                        const isActive = code === locale;
                        return (
                            <li
                                key={code}
                                role="option"
                                aria-selected={isActive}
                                className={`locale-switcher__item${isActive ? ' is-active' : ''}`}
                                onClick={() => {
                                    setOpen(false);
                                    if (!isActive) setLocale(code);
                                }}
                            >
                                <span className="locale-switcher__native">{meta.native}</span>
                                <span className="locale-switcher__code">{code}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default LocaleSwitcher;
